import { useState } from 'react';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import type { Ask, UpdateAskRequest } from '@spi/shared';
import { SeedFormat, SeedTreatment, AskStatus } from '@spi/shared';
import { StatusBadge } from '../shared/StatusBadge';
import { FulfillAskModal } from '../donations/FulfillAskModal';
import { ConfirmDialog } from '../shared/ConfirmDialog';
import { useAuth } from '../../../hooks/useAuth';
import { UserRole } from '@spi/shared';
import { AskIcon } from '../shared/EntityIcons';
import { useUpdateAsk, useCancelAsk } from '../../../api/asks';
import { useReferenceLists } from '../../../api/referenceLists';
import { useAskMessages, usePostAskMessage } from '../../../api/messages';
import { MessageThread } from '../messages/MessageThread';

export function AskCard({ ask, projectRecipientId, projectId }: { ask: Ask; projectRecipientId: number; projectId: number }) {
  const { user, isAuthenticated } = useAuth();
  const [showFulfill, setShowFulfill] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const { data: askMessages, isLoading: askMessagesLoading } = useAskMessages(isAuthenticated ? ask.id : 0);
  const postAskMessage = usePostAskMessage(ask.id);
  const updateAsk = useUpdateAsk(projectId);
  const cancelAsk = useCancelAsk(projectId);

  const isDonor = user?.role === UserRole.DONOR;
  const isAdmin = user?.role === UserRole.ADMIN;
  const isOwner = user?.id === projectRecipientId;
  const isOpen = ask.status === AskStatus.OPEN || ask.status === AskStatus.PARTIALLY_FULFILLED;
  const canPledge = isAuthenticated && isDonor && isOpen && user?.id !== projectRecipientId;
  const canEdit = isOwner && isOpen;
  const canCancel = (isOwner || isAdmin) && ask.status !== AskStatus.FULFILLED && ask.status !== AskStatus.CANCELLED;

  const formatLabel = ask.seedFormat === SeedFormat.BULK ? 'Bulk' : ask.seedFormat === SeedFormat.PACKET ? 'Packet' : 'Bulk + Packet';

  return (
    <>
      <div className="card p-5">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div>
            <h4 className="font-semibold text-spi-charcoal flex items-center gap-1.5">
              <AskIcon className="w-4 h-4 text-spi-green" />
              {ask.crop}
            </h4>
            {ask.preferredVarieties && (
              <p className="text-xs text-spi-slate">Varieties: {ask.preferredVarieties}</p>
            )}
          </div>
          <StatusBadge status={ask.status} />
        </div>

        <div className="flex flex-wrap gap-2 mb-3">
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-spi-seed text-spi-earth">
            {formatLabel}
          </span>
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-spi-seed text-spi-earth">
            {ask.seedTreatment === 'treated' ? 'Treated' : 'Untreated'}
          </span>
        </div>

        {/* Quantity progress */}
        {(ask.seedFormat === SeedFormat.BULK || ask.seedFormat === SeedFormat.BOTH) && ask.quantityKg && (
          <div className="mb-2">
            <div className="flex justify-between text-xs text-spi-slate mb-1">
              <span>Bulk: {ask.fulfilledKg} / {ask.quantityKg} kg</span>
              <span>{Math.min(100, Math.round((ask.fulfilledKg / ask.quantityKg) * 100))}%</span>
            </div>
            <div className="w-full h-2 bg-spi-seed rounded-full overflow-hidden">
              <div className="h-full bg-spi-green rounded-full transition-all" style={{ width: `${Math.min(100, (ask.fulfilledKg / ask.quantityKg) * 100)}%` }} />
            </div>
          </div>
        )}
        {(ask.seedFormat === SeedFormat.PACKET || ask.seedFormat === SeedFormat.BOTH) && ask.quantityPackets && (
          <div className="mb-2">
            <div className="flex justify-between text-xs text-spi-slate mb-1">
              <span>Packets: {ask.fulfilledPackets} / {ask.quantityPackets}</span>
              <span>{Math.min(100, Math.round((ask.fulfilledPackets / ask.quantityPackets) * 100))}%</span>
            </div>
            <div className="w-full h-2 bg-spi-seed rounded-full overflow-hidden">
              <div className="h-full bg-spi-orange rounded-full transition-all" style={{ width: `${Math.min(100, (ask.fulfilledPackets / ask.quantityPackets) * 100)}%` }} />
            </div>
          </div>
        )}

        {ask.acceptableTreatments.length > 0 && (
          <p className="text-xs text-spi-slate mt-2">Acceptable: {ask.acceptableTreatments.join(', ')}</p>
        )}
        {ask.unacceptableTreatments.length > 0 && (
          <p className="text-xs text-red-500 mt-1">Not acceptable: {ask.unacceptableTreatments.join(', ')}</p>
        )}

        {/* Delivery address override */}
        {ask.deliveryAddress && (
          <p className="text-xs text-spi-slate mt-2 flex items-center gap-1">
            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" /></svg>
            Ship to: {ask.deliveryAddress}{ask.deliveryCountry ? `, ${ask.deliveryCountry}` : ''}
          </p>
        )}

        {/* Location indicator */}
        {ask.latitude != null && ask.longitude != null && (
          <p className="text-xs text-spi-slate mt-2 flex items-center gap-1">
            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" /></svg>
            Custom location ({ask.latitude.toFixed(4)}, {ask.longitude.toFixed(4)})
          </p>
        )}

        {canPledge && (
          <button onClick={() => setShowFulfill(true)} className="btn btn-primary text-sm mt-3 w-full">
            Pledge Seeds
          </button>
        )}

        {/* Owner/Admin actions */}
        {(canEdit || canCancel) && (
          <div className="flex gap-2 mt-3">
            {canEdit && (
              <button onClick={() => setShowEdit(true)} className="btn btn-outline text-xs py-1.5 px-3 flex-1">
                Edit
              </button>
            )}
            {canCancel && (
              <button onClick={() => setShowCancelConfirm(true)} className="text-xs py-1.5 px-3 rounded-xl border border-red-200 text-red-600 hover:bg-red-50 font-semibold flex-1">
                Cancel
              </button>
            )}
          </div>
        )}

        {isAuthenticated && (
          <div className="mt-3 pt-3 border-t border-spi-parchment">
            <MessageThread
              messages={askMessages?.data || []}
              isLoading={askMessagesLoading}
              onPost={async (body, files) => { await postAskMessage.mutateAsync({ body, files }); }}
              isPosting={postAskMessage.isPending}
              title="Questions"
              placeholder="Ask a question about this seed request..."
              collapsible
              contextType="ask"
              contextId={ask.id}
            />
          </div>
        )}
      </div>

      {showFulfill && (
        <FulfillAskModal ask={ask} onClose={() => setShowFulfill(false)} />
      )}

      {showEdit && (
        <EditAskModal
          ask={ask}
          onClose={() => setShowEdit(false)}
          onSaved={() => setShowEdit(false)}
          updateAsk={updateAsk}
        />
      )}

      <ConfirmDialog
        open={showCancelConfirm}
        title="Cancel Seed Request"
        message={`Are you sure you want to cancel the ${ask.crop} seed request? This cannot be undone.`}
        confirmLabel="Cancel Request"
        danger
        onConfirm={async () => {
          try {
            await cancelAsk.mutateAsync(ask.id);
            toast.success('Seed request cancelled');
            setShowCancelConfirm(false);
          } catch (err) {
            toast.error((err as Error).message || 'Failed to cancel');
          }
        }}
        onCancel={() => setShowCancelConfirm(false)}
      />
    </>
  );
}

function EditAskModal({ ask, onClose, onSaved, updateAsk }: {
  ask: Ask;
  onClose: () => void;
  onSaved: () => void;
  updateAsk: ReturnType<typeof useUpdateAsk>;
}) {
  const { data: refLists } = useReferenceLists();
  const { register, handleSubmit, watch } = useForm<UpdateAskRequest>({
    defaultValues: {
      crop: ask.crop,
      preferredVarieties: ask.preferredVarieties || undefined,
      seedFormat: ask.seedFormat,
      quantityKg: ask.quantityKg ?? undefined,
      quantityPackets: ask.quantityPackets ?? undefined,
      seedTreatment: ask.seedTreatment,
      acceptableTreatments: ask.acceptableTreatments,
      unacceptableTreatments: ask.unacceptableTreatments,
      deliveryAddress: ask.deliveryAddress,
      deliveryCountry: ask.deliveryCountry,
      latitude: ask.latitude,
      longitude: ask.longitude,
    },
  });

  const seedFormat = watch('seedFormat');
  const seedTreatment = watch('seedTreatment');

  const onSubmit = async (data: UpdateAskRequest) => {
    try {
      if (data.latitude != null && isNaN(data.latitude)) data.latitude = null;
      if (data.longitude != null && isNaN(data.longitude)) data.longitude = null;
      await updateAsk.mutateAsync({ id: ask.id, data });
      toast.success('Seed request updated');
      onSaved();
    } catch (err) {
      toast.error((err as Error).message || 'Failed to update');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl p-6 max-w-lg w-full mx-4 shadow-2xl max-h-[90vh] overflow-y-auto">
        <h3 className="text-lg font-semibold text-spi-charcoal mb-4">Edit Seed Request</h3>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div>
            <label className="input-label">Crop *</label>
            <select {...register('crop')} className="input">
              {refLists?.crop?.map((item) => (
                <option key={item.id} value={item.value}>{item.value}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="input-label">Preferred Varieties</label>
            <input {...register('preferredVarieties')} className="input" placeholder="e.g., NERICA 4, Kigega" />
          </div>

          <div>
            <label className="input-label">Seed Format *</label>
            <div className="flex gap-4">
              {Object.values(SeedFormat).map((format) => (
                <label key={format} className="flex items-center gap-2">
                  <input type="radio" {...register('seedFormat')} value={format} className="text-spi-green" />
                  <span className="text-sm capitalize">{format}</span>
                </label>
              ))}
            </div>
          </div>

          {(seedFormat === SeedFormat.BULK || seedFormat === SeedFormat.BOTH) && (
            <div>
              <label className="input-label">Quantity (kg)</label>
              <input type="number" step="0.1" {...register('quantityKg', { valueAsNumber: true })} className="input" />
            </div>
          )}

          {(seedFormat === SeedFormat.PACKET || seedFormat === SeedFormat.BOTH) && (
            <div>
              <label className="input-label">Quantity (packets)</label>
              <input type="number" {...register('quantityPackets', { valueAsNumber: true })} className="input" />
            </div>
          )}

          <div>
            <label className="input-label">Seed Treatment *</label>
            <div className="flex gap-4">
              {Object.values(SeedTreatment).map((treatment) => (
                <label key={treatment} className="flex items-center gap-2">
                  <input type="radio" {...register('seedTreatment')} value={treatment} className="text-spi-green" />
                  <span className="text-sm capitalize">{treatment}</span>
                </label>
              ))}
            </div>
          </div>

          {seedTreatment === SeedTreatment.TREATED && refLists?.treatment && (
            <div>
              <label className="input-label">Acceptable Treatments</label>
              <div className="flex flex-wrap gap-2">
                {refLists.treatment.map((item) => (
                  <label key={item.id} className="flex items-center gap-1.5 text-sm">
                    <input type="checkbox" {...register('acceptableTreatments')} value={item.value} />
                    {item.value}
                  </label>
                ))}
              </div>
            </div>
          )}

          {refLists?.treatment && (
            <div>
              <label className="input-label">Unacceptable Treatments</label>
              <div className="flex flex-wrap gap-2">
                {refLists.treatment.map((item) => (
                  <label key={item.id} className="flex items-center gap-1.5 text-sm">
                    <input type="checkbox" {...register('unacceptableTreatments')} value={item.value} />
                    {item.value}
                  </label>
                ))}
              </div>
            </div>
          )}

          <div className="border-t pt-4">
            <label className="input-label text-spi-slate">Optional: Delivery address override</label>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-spi-slate">Address</label>
                <input {...register('deliveryAddress')} className="input" placeholder="e.g., 123 Farm Road" />
              </div>
              <div>
                <label className="text-xs text-spi-slate">Country</label>
                <input {...register('deliveryCountry')} className="input" placeholder="e.g., Uganda" />
              </div>
            </div>
          </div>

          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="btn btn-outline flex-1">Cancel</button>
            <button type="submit" disabled={updateAsk.isPending} className="btn btn-primary flex-1">
              {updateAsk.isPending ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
